import { RefreshCw, Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/utils";

type LoadingSpinnerProps = {
  /** Taille du spinner */
  size?: "sm" | "md" | "lg" | "xl"
  /** Variante du spinner */
  variant?: "default" | "primary" | "secondary"
  /** Type d'icône */
  type?: "spinner" | "refresh"
  /** Texte à afficher sous le spinner */
  text?: string
  /** Classes CSS personnalisées */
  className?: string
  /** Centrer le spinner */
  centered?: boolean
};

/**
 * Composant spinner de chargement réutilisable
 */
const LoadingSpinner = ({
  size = "md",
  variant = "default",
  type = "spinner",
  text,
  className,
  centered = false,
}: LoadingSpinnerProps) => {
  const getSizeClasses = (size: LoadingSpinnerProps["size"]) => {
    switch (size) {
      case "sm":
        return "w-4 h-4";
      case "lg":
        return "w-8 h-8";
      case "xl":
        return "w-12 h-12";
      default: // md
        return "w-6 h-6";
    }
  };

  const getVariantClasses = (variant: LoadingSpinnerProps["variant"]) => {
    switch (variant) {
      case "primary":
        return "text-blue-600";
      case "secondary":
        return "text-gray-500";
      default:
        return "text-gray-600";
    }
  };

  const getTextSize = (size: LoadingSpinnerProps["size"]) => {
    switch (size) {
      case "sm":
        return "text-xs";
      case "lg":
        return "text-base";
      case "xl":
        return "text-lg";
      default: // md
        return "text-sm";
    }
  };

  const SpinnerIcon = type === "refresh" ? RefreshCw : Loader2;
  const sizeClasses = getSizeClasses(size);
  const variantClasses = getVariantClasses(variant);
  const textSizeClasses = getTextSize(size);

  const content = (
    <div className={cn(
      "inline-flex flex-col items-center space-y-2",
      className,
    )}
    >
      <SpinnerIcon
        className={cn(
          "animate-spin",
          sizeClasses,
          variantClasses,
        )}
      />
      {text && (
        <span className={cn(
          "font-medium",
          textSizeClasses,
          variantClasses,
        )}
        >
          {text}
        </span>
      )}
    </div>
  );

  if (centered) {
    return (
      <div className="flex justify-center items-center min-h-[100px]">
        {content}
      </div>
    );
  }

  return content;
};

export default LoadingSpinner;
